import { armVariant, DEFAULT_HERO, HERO_TESTS, type HeroTest, runningTest } from './tests.ts'
import { HERO_VERSIONS, type HeroVersion } from './versions.ts'

/**
 * How the visitor got their hero:
 * - `random`: drawn now for the running test,
 * - `stored`: drawn on an earlier visit of this browser,
 * - `url`: forced by `?v=` (a test arm `t1.h3` or a bare version `h3`),
 * - `default`: no test runs, or `?v=` is something else (a campaign tag): outside any test.
 */
export type HeroAssignment = 'random' | 'stored' | 'url' | 'default'

export type HeroChoice = {
  version: string
  /** Value for `leads.variant` / `page_views.variant`; null = none. */
  variant: string | null
  test: string | null
  assignment: HeroAssignment
}

export type ArmStore = {
  get: (testId: string) => string | null
  set: (testId: string, version: string) => void
}

type Options = {
  tests?: ReadonlyArray<HeroTest>
  versions?: ReadonlyArray<HeroVersion>
  defaultHero?: string
  store: ArmStore
  random: () => number
}

export function chooseHero(v: string | undefined, opts: Options): HeroChoice {
  const tests = opts.tests ?? HERO_TESTS
  const versions = opts.versions ?? HERO_VERSIONS
  const defaultHero = opts.defaultHero ?? DEFAULT_HERO
  const running = runningTest(tests)
  const exists = (id: string) => versions.some((x) => x.id === id)

  if (v) {
    for (const test of tests) {
      const arm = test.arms.find((a) => armVariant(test, a.version) === v)
      if (!arm) continue
      // A forced arm also sticks, unless this browser already drew one: the first exposure wins.
      if (test === running && !opts.store.get(test.id)) opts.store.set(test.id, arm.version)
      return { version: arm.version, variant: v, test: test.id, assignment: 'url' }
    }
    if (exists(v)) return { version: v, variant: v, test: null, assignment: 'url' }
    return { version: defaultHero, variant: v, test: null, assignment: 'default' }
  }

  if (!running) return { version: defaultHero, variant: null, test: null, assignment: 'default' }

  const stored = opts.store.get(running.id)
  const storedArm = running.arms.find((a) => a.version === stored)
  if (storedArm) {
    return {
      version: storedArm.version,
      variant: armVariant(running, storedArm.version),
      test: running.id,
      assignment: 'stored',
    }
  }
  const version = drawArm(running, opts.random())
  opts.store.set(running.id, version)
  return { version, variant: armVariant(running, version), test: running.id, assignment: 'random' }
}

/** Weighted draw; `r` in [0, 1). */
export function drawArm(test: HeroTest, r: number): string {
  const total = test.arms.reduce((sum, a) => sum + a.weight, 0)
  let point = r * total
  for (const arm of test.arms) {
    point -= arm.weight
    if (point < 0) return arm.version
  }
  // r * total can round up to total.
  return test.arms[test.arms.length - 1]?.version ?? DEFAULT_HERO
}

const STORE_PREFIX = 'tb_ab_'

/**
 * localStorage so the arm survives a new tab or a return the next day; without storage (private
 * mode, blocked) each page load draws again — rare, and still random, so no bias.
 */
export const browserArmStore: ArmStore = {
  get: (testId) => {
    try {
      return localStorage.getItem(STORE_PREFIX + testId)
    } catch {
      return null
    }
  },
  set: (testId, version) => {
    try {
      localStorage.setItem(STORE_PREFIX + testId, version)
    } catch {
      // Storage blocked: the visit keeps its draw for this page load only.
    }
  },
}
